import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useArtSync } from '../hooks/useArtSync';
import { useSearch } from '../hooks/useSearch';
import './RemoteView.css';

export default function RemoteView() {
  const { currentUri, setArtwork, settings, setSettings } = useArtSync();

  // Memoize filters object to prevent infinite loop
  const filters = useMemo(() => ({
    artistFilter: settings.artistFilter || '',
    movementFilter: settings.movementFilter || '',
    mediumFilter: settings.mediumFilter || ''
  }), [settings.artistFilter, settings.movementFilter, settings.mediumFilter]);

  const { query, setQuery, results, isLoading: isSearching, hasMore, loadMore } = useSearch(filters);
  const [artworkData, setArtworkData] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [draftFilters, setDraftFilters] = useState({
    movementFilter: '',
    artistFilter: '',
    mediumFilter: ''
  });

  // Transition state for graceful artwork changes
  const [pendingThumbnail, setPendingThumbnail] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(null);

  // Related works state (separate from search)
  const [relatedWorks, setRelatedWorks] = useState([]);
  const [isLoadingRelated, setIsLoadingRelated] = useState(false);

  // Gateway query state with pagination
  const [gatewayInfo, setGatewayInfo] = useState(null);
  const [gatewayCursor, setGatewayCursor] = useState(null);
  const [gatewayHasMore, setGatewayHasMore] = useState(false);

  // Infinite scroll sentinel ref
  const sentinelRef = useRef(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'https://artfolio-api.spb-f86.workers.dev';

  // Handle gateway navigation with structured URIs
  const handleGatewayClick = useCallback(async (type, label, uri, wikidataId) => {
    // Clear search query to show gateway results instead
    setQuery('');
    setIsLoadingRelated(true);
    setRelatedWorks([]);
    setGatewayInfo({ type, label, uri });
    setGatewayCursor(null);

    try {
      // Build gateway query URL
      let gatewayUrl = `${apiUrl}?gateway=${encodeURIComponent(type)}&label=${encodeURIComponent(label)}`;
      if (uri) gatewayUrl += `&uri=${encodeURIComponent(uri)}`;
      if (wikidataId) gatewayUrl += `&wikidata=${encodeURIComponent(wikidataId)}`;

      // Add filters
      if (filters.artistFilter) gatewayUrl += `&artistFilter=${encodeURIComponent(filters.artistFilter)}`;
      if (filters.movementFilter) gatewayUrl += `&movementFilter=${encodeURIComponent(filters.movementFilter)}`;
      if (filters.mediumFilter) gatewayUrl += `&mediumFilter=${encodeURIComponent(filters.mediumFilter)}`;

      const res = await fetch(gatewayUrl);
      const data = await res.json();

      if (data.results) {
        setRelatedWorks(data.results);
        setGatewayInfo(prev => ({ ...prev, entity: data.gateway?.entity }));
        setGatewayCursor(data.nextCursor || null);
        setGatewayHasMore(data.hasMore || false);
      }
    } catch (e) {
      console.error("Failed to load gateway results", e);
      setRelatedWorks([]);
    } finally {
      setIsLoadingRelated(false);
    }
  }, [apiUrl, setQuery, filters]);

  // Load more gateway results for infinite scroll
  const loadMoreGateway = useCallback(async () => {
    if (!gatewayInfo || !gatewayCursor || !gatewayHasMore || isLoadingRelated) return;

    setIsLoadingRelated(true);

    try {
      let gatewayUrl = `${apiUrl}?gateway=${encodeURIComponent(gatewayInfo.type)}&label=${encodeURIComponent(gatewayInfo.label)}&cursor=${encodeURIComponent(gatewayCursor)}`;
      if (gatewayInfo.uri) gatewayUrl += `&uri=${encodeURIComponent(gatewayInfo.uri)}`;

      // Add filters
      if (filters.artistFilter) gatewayUrl += `&artistFilter=${encodeURIComponent(filters.artistFilter)}`;
      if (filters.movementFilter) gatewayUrl += `&movementFilter=${encodeURIComponent(filters.movementFilter)}`;
      if (filters.mediumFilter) gatewayUrl += `&mediumFilter=${encodeURIComponent(filters.mediumFilter)}`;

      const res = await fetch(gatewayUrl);
      const data = await res.json();

      if (data.results) {
        setRelatedWorks(prev => [...prev, ...data.results]);
        setGatewayCursor(data.nextCursor || null);
        setGatewayHasMore(data.hasMore || false);
      }
    } catch (e) {
      console.error("Failed to load more gateway results", e);
    } finally {
      setIsLoadingRelated(false);
    }
  }, [apiUrl, gatewayInfo, gatewayCursor, gatewayHasMore, isLoadingRelated, filters]);

  // Fetch details when currentUri changes
  useEffect(() => {
    if (!currentUri) {
      setArtworkData(null);
      setIsTransitioning(false);
      return;
    }

    const fetchDetails = async () => {
      setIsLoadingDetails(true);
      setRelatedWorks([]); // Clear old related works
      try {
        const res = await fetch(`${apiUrl}?uri=${encodeURIComponent(currentUri)}`);
        const data = await res.json();
        console.log("Artwork data loaded:", data);
        if (data && !data.error) {
          setArtworkData(data);
          // Update background with full image once loaded
          if (data.image?.url) {
            setBackgroundImage(data.image.url);
          }
        } else {
          console.error("Invalid artwork data:", data);
          setArtworkData(null);
        }
      } catch (e) {
        console.error("Failed to load remote details", e);
        setArtworkData(null);
      } finally {
        setIsLoadingDetails(false);
        // End transition after a brief delay to allow card to animate
        setTimeout(() => {
          setIsTransitioning(false);
          setPendingThumbnail(null);
        }, 300);
      }
    };

    fetchDetails();
  }, [currentUri, apiUrl]);

  // Calculate these values early for use in effects
  const isSearchActive = query.trim().length > 0;
  const isGatewayActive = gatewayInfo !== null && !isSearchActive;

  // Fetch related works only when explicitly viewing gateway results
  // (Don't auto-fetch on every artwork change to preserve search results)
  useEffect(() => {
    // Only fetch related works if we're viewing gateway results or no search/gateway is active
    if (isGatewayActive || (!isSearchActive && !gatewayInfo)) {
      if (!artworkData?.artist?.name || artworkData.artist.name === 'Unknown') {
        setRelatedWorks([]);
        return;
      }

      const fetchRelated = async () => {
        setIsLoadingRelated(true);
        try {
          let searchUrl = `${apiUrl}?search=${encodeURIComponent(artworkData.artist.name)}`;

          // Add filters
          if (filters.artistFilter) searchUrl += `&artistFilter=${encodeURIComponent(filters.artistFilter)}`;
          if (filters.movementFilter) searchUrl += `&movementFilter=${encodeURIComponent(filters.movementFilter)}`;
          if (filters.mediumFilter) searchUrl += `&mediumFilter=${encodeURIComponent(filters.mediumFilter)}`;

          const res = await fetch(searchUrl);
          const data = await res.json();
          // Filter out the current artwork from related works
          const filtered = (data.results || []).filter(item => item.uri !== currentUri);
          setRelatedWorks(filtered);
        } catch (e) {
          console.error("Failed to load related works", e);
          setRelatedWorks([]);
        } finally {
          setIsLoadingRelated(false);
        }
      };

      fetchRelated();
    }
  }, [artworkData, currentUri, apiUrl, filters, isSearchActive, isGatewayActive, gatewayInfo]);

  // Intersection Observer for infinite scroll (search or gateway results)
  useEffect(() => {
    if (!sentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;

        // If search is active, load more search results
        if (query.trim() && hasMore && !isSearching) {
          loadMore();
        }
        // Otherwise, if gateway is active, load more gateway results
        else if (!query.trim() && gatewayHasMore && !isLoadingRelated) {
          loadMoreGateway();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [query, hasMore, isSearching, loadMore, gatewayHasMore, isLoadingRelated, loadMoreGateway]);

  const handleResultClick = useCallback((uri, thumbnail) => {
    // Start graceful transition
    setIsTransitioning(true);
    setPendingThumbnail(thumbnail);

    // Fade in new background immediately
    if (thumbnail) {
      setBackgroundImage(thumbnail);
    }

    // Populate queue from current results (search or gateway)
    const currentResults = query.trim() ? results : relatedWorks;
    if (currentResults.length > 0) {
      // Find index of clicked artwork
      const clickedIndex = currentResults.findIndex(item => item.uri === uri);
      // Create queue starting from clicked item
      const queue = currentResults.map(item => item.uri);
      setSettings({
        ...settings,
        artworkQueue: queue,
        queueIndex: clickedIndex >= 0 ? clickedIndex : 0
      });
    }

    // Set the artwork (triggers fetch)
    setArtwork(uri);
    setQuery(''); // Clear search to show related works
  }, [setArtwork, setQuery, results, relatedWorks, query, settings, setSettings]);

  // Auto-populate filters when opening settings
  const handleOpenSettings = useCallback(() => {
    if (artworkData) {
      // Auto-populate with current artwork data
      const firstMovement = artworkData.artist?.movements?.[0] || '';
      const artistName = artworkData.artist?.name !== 'Unknown' ? artworkData.artist.name : '';
      const medium = artworkData.details?.labelLine || '';

      setDraftFilters({
        movementFilter: settings.movementFilter || firstMovement,
        artistFilter: settings.artistFilter || artistName,
        mediumFilter: settings.mediumFilter || medium
      });
    } else {
      setDraftFilters({
        movementFilter: settings.movementFilter || '',
        artistFilter: settings.artistFilter || '',
        mediumFilter: settings.mediumFilter || ''
      });
    }
    setShowSettings(true);
  }, [artworkData, settings]);

  // Quick filter apply functions - use filter value AS the search query
  const applyArtistFilter = useCallback((artistName) => {
    setSettings({
      ...settings,
      artistFilter: artistName
    });
    // Search for the artist name directly
    setQuery(artistName);
  }, [settings, setSettings, setQuery]);

  const applyMovementFilter = useCallback((movement) => {
    setSettings({
      ...settings,
      movementFilter: movement
    });
    // Search for the movement directly
    setQuery(movement);
  }, [settings, setSettings, setQuery]);

  const clearArtistFilter = useCallback(() => {
    setSettings({ ...settings, artistFilter: '' });
  }, [settings, setSettings]);

  const clearMovementFilter = useCallback(() => {
    setSettings({ ...settings, movementFilter: '' });
  }, [settings, setSettings]);

  const clearMediumFilter = useCallback(() => {
    setSettings({ ...settings, mediumFilter: '' });
  }, [settings, setSettings]);

  // Apply gateway as filter based on type - search for the filter value directly
  const applyGatewayFilter = useCallback((type, label) => {
    if (type === 'movement') {
      applyMovementFilter(label);
    } else if (type === 'artist') {
      applyArtistFilter(label);
    } else {
      // All other gateway types (material, subject, classification, culture, period, etc.)
      // apply to medium filter AND search for it directly
      setSettings({
        ...settings,
        mediumFilter: label
      });
      // Search for the label directly (e.g., "Oil on canvas", "Landscape")
      setQuery(label);
    }
  }, [settings, setSettings, applyMovementFilter, applyArtistFilter, setQuery]);

  const shouldMinimizeCard = isSearchActive || isGatewayActive;
  const hasActiveFilters = settings.movementFilter || settings.artistFilter || settings.mediumFilter;

  return (
    <div className="remote-container">
      {/* Background Image Layer with crossfade */}
      {backgroundImage && (
        <div className="remote-background" key={backgroundImage}>
          <img
            src={backgroundImage}
            alt=""
            className="remote-background-image"
          />
        </div>
      )}

      <main className="remote-content">

        {/* SEARCH BAR WITH BUTTONS */}
        <section className="search-section">
          <div className="search-bar-container">
            <div className="search-input-wrapper">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.target.blur(); // Hide keyboard on mobile
                  }
                }}
                placeholder="Search artworks..."
                className="search-input"
              />
              {isSearchActive && (
                <button onClick={() => setQuery('')} className="search-clear-btn">
                  ✕
                </button>
              )}
            </div>
            <div className="search-actions">
              <button
                className="settings-btn"
                onClick={() => setSettings({ ...settings, showDisplayDetails: !settings.showDisplayDetails })}
                title="Toggle details panel on display"
              >
                ℹ️
              </button>
              <button
                className="settings-btn"
                onClick={handleOpenSettings}
                title="Settings"
              >
                ⚙️
              </button>
            </div>
          </div>
        </section>

        {/* ACTIVE FILTERS BAR */}
        {hasActiveFilters && (
          <section className="active-filters-bar">
            {settings.artistFilter && (
              <div className="filter-chip">
                <span className="filter-chip-icon">👤</span>
                <span className="filter-chip-label">{settings.artistFilter}</span>
                <button
                  className="filter-chip-remove"
                  onClick={clearArtistFilter}
                  title="Remove artist filter"
                >
                  ✕
                </button>
              </div>
            )}
            {settings.movementFilter && (
              <div className="filter-chip">
                <span className="filter-chip-icon">🎨</span>
                <span className="filter-chip-label">{settings.movementFilter}</span>
                <button
                  className="filter-chip-remove"
                  onClick={clearMovementFilter}
                  title="Remove movement filter"
                >
                  ✕
                </button>
              </div>
            )}
            {settings.mediumFilter && (
              <div className="filter-chip">
                <span className="filter-chip-icon">🖼</span>
                <span className="filter-chip-label">{settings.mediumFilter}</span>
                <button
                  className="filter-chip-remove"
                  onClick={clearMediumFilter}
                  title="Remove medium filter"
                >
                  ✕
                </button>
              </div>
            )}
          </section>
        )}

        {/* NOW PLAYING CARD */}
        <section className={`now-playing-card ${shouldMinimizeCard || isTransitioning ? 'minimized' : ''} ${isTransitioning ? 'transitioning' : ''}`}>

           {!artworkData && !isLoadingDetails && !currentUri && !isTransitioning && (
               <div className="card-placeholder">
                   Search for an artwork to begin
               </div>
           )}

           {(isLoadingDetails || isTransitioning) && (
               <div className="card-minimized card-loading">
                 {pendingThumbnail && (
                   <img
                     src={pendingThumbnail}
                     alt=""
                     className="minimized-thumbnail"
                   />
                 )}
                 <div className="minimized-info">
                   <div className="minimized-title">Loading...</div>
                 </div>
                 <div className="loading-spinner" />
               </div>
           )}

           {/* Minimized view when searching or gateway active */}
           {artworkData && !isLoadingDetails && !isTransitioning && shouldMinimizeCard && (
               <div
                 className="card-minimized"
                 onClick={() => {
                   setQuery('');
                   setGatewayInfo(null);
                 }}
                 title="Click to expand"
               >
                 <img
                   src={artworkData.image?.url}
                   alt={artworkData.title}
                   className="minimized-thumbnail"
                 />
                 <div className="minimized-info">
                   <div className="minimized-title">{artworkData.title}</div>
                   <div className="minimized-artist">{artworkData.artist?.name}</div>
                 </div>
                 <div className="minimized-action">▼</div>
               </div>
           )}

           {/* Full card view when not minimized */}
           {artworkData && !isLoadingDetails && !isTransitioning && !shouldMinimizeCard && (
               <div className="card-content">
                   {/* Image Preview */}
                   <div className="image-container">
                       <img
                            src={artworkData.image?.url}
                            alt={artworkData.title}
                            className="artwork-image"
                       />
                       <div className="gradient-overlay" />
                       <div className="card-text">
                           <h2 className="artwork-title">
                               {artworkData.title}
                           </h2>
                           <p className="artwork-artist">
                               {artworkData.artist?.name}
                           </p>
                       </div>
                   </div>

                   {/* ARTIST INFO SECTION */}
                   {artworkData.artist?.name && artworkData.artist.name !== 'Unknown' && (
                     <div
                       className="info-section"
                       onClick={() => handleGatewayClick(
                         'artist',
                         artworkData.artist.name,
                         artworkData.artist.ulanUrl,
                         artworkData.artist.wikidataUrl
                       )}
                     >
                       <h3 className="info-section-title">
                         <span className="info-icon">👤</span>
                         Artist
                         <button
                           className="quick-filter-btn"
                           onClick={(e) => {
                             e.stopPropagation();
                             applyArtistFilter(artworkData.artist.name);
                           }}
                           title="Filter by this artist"
                         >
                           🔍 Filter
                         </button>
                       </h3>
                       <div className="info-content">
                         <div className="artist-name-line">{artworkData.artist.name}</div>
                         {(artworkData.artist.birthYear || artworkData.artist.deathYear) && (
                           <div className="artist-dates">
                             {artworkData.artist.birthYear || '?'} – {artworkData.artist.deathYear || 'present'}
                           </div>
                         )}
                         {artworkData.artist.nationality && (
                           <div className="artist-nationality">{artworkData.artist.nationality}</div>
                         )}

                         {/* Wikidata Biography */}
                         {artworkData.artist.biography && (
                           <div className="artist-biography">{artworkData.artist.biography}</div>
                         )}

                         {/* Art Movements */}
                         {artworkData.artist.movements?.length > 0 && (
                           <div className="artist-movements">
                             <span className="movements-label">Movements:</span>
                             <div className="movements-tags">
                               {artworkData.artist.movements.map((movement, i) => (
                                 <button
                                   key={i}
                                   className="movement-tag"
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     applyMovementFilter(movement);
                                   }}
                                   title="Click to filter by this movement"
                                 >
                                   {movement}
                                 </button>
                               ))}
                             </div>
                           </div>
                         )}

                         {/* Influences */}
                         {artworkData.artist.influences?.length > 0 && (
                           <div className="artist-influences">
                             <span className="influences-label">Influenced by:</span>
                             <div className="influences-tags">
                               {artworkData.artist.influences.slice(0, 5).map((influence, i) => (
                                 <button
                                   key={i}
                                   className="influence-tag"
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     handleGatewayClick('artist', influence, null, null);
                                   }}
                                 >
                                   {influence}
                                 </button>
                               ))}
                             </div>
                           </div>
                         )}
                       </div>
                     </div>
                   )}

                   {/* ARTWORK DETAILS SECTION */}
                   {artworkData.details && (
                     <div className="info-section info-section-static">
                       <h3 className="info-section-title">
                         <span className="info-icon">🖼</span>
                         About this work
                       </h3>
                       <div className="info-content">
                         {artworkData.details.description && (
                           <div className="detail-description">{artworkData.details.description}</div>
                         )}
                         {artworkData.details.dimensions && (
                           <div className="detail-row">
                             <span className="detail-label">Dimensions</span>
                             <span className="detail-value">{artworkData.details.dimensions}</span>
                           </div>
                         )}
                         {artworkData.details.labelLine && (
                           <div className="detail-row">
                             <span className="detail-label">Label</span>
                             <span className="detail-value">{artworkData.details.labelLine}</span>
                           </div>
                         )}
                         {artworkData.details.creditLine && (
                           <div className="detail-row">
                             <span className="detail-label">Credit</span>
                             <span className="detail-value">{artworkData.details.creditLine}</span>
                           </div>
                         )}
                         {artworkData.details.source && (
                           <div className="detail-source">
                             Source: <span className="source-name">{artworkData.details.source.toUpperCase()}</span>
                           </div>
                         )}
                       </div>
                     </div>
                   )}

                   {/* GATEWAYS (Tags) */}
                   <div className="gateways-container">
                       <h3 className="gateway-title">Explore</h3>
                       <div className="tags-wrapper">
                           {/* Concept Gateways - click to apply as filter */}
                           {artworkData.gateways?.map((gw, i) => (
                               <button
                                   key={i}
                                   onClick={() => applyGatewayFilter(gw.type, gw.label)}
                                   className="tag-btn tag-concept"
                                   title="Click to filter by this"
                               >
                                   {gw.label}
                               </button>
                           ))}
                           {(!artworkData.gateways || artworkData.gateways.length === 0) && (
                               <span className="card-placeholder" style={{fontSize: '0.75rem', padding: 0}}>No tags available</span>
                           )}
                       </div>
                   </div>
               </div>
           )}
        </section>

        {/* SEARCH RESULTS - Show when actively searching */}
        {isSearchActive && (
          <section className="search-results-section">
            <h3 className="section-title">
              {isSearching && results.length === 0 ? 'Searching...' : `Results for "${query}"`}
            </h3>

            {/* Loading overlay when searching and no results yet */}
            {isSearching && results.length === 0 && (
              <div className="search-loading-overlay">
                <div className="search-loading-spinner" />
                <div className="search-loading-text">Searching museums...</div>
              </div>
            )}

            {results.length > 0 && (
              <div className="search-results-grid">
                {results.map((item, index) => (
                  <button
                    key={`${item.source}-${item.uri}-${index}`}
                    onClick={() => handleResultClick(item.uri, item.thumbnail)}
                    className="search-result-item"
                  >
                    <div className="result-thumbnail-wrapper">
                      <img
                        src={item.thumbnail}
                        alt={item.title}
                        className="result-thumbnail"
                        loading="lazy"
                      />
                      <span className={`source-badge source-${item.source}`}>
                        {item.source}
                      </span>
                    </div>
                    <div className="result-info">
                      <div className="result-title">{item.title}</div>
                      <div className="result-artist">{item.artist}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Infinite scroll sentinel */}
            {hasMore && (
              <div ref={sentinelRef} className="scroll-sentinel">
                {isSearching && <span>Loading more...</span>}
              </div>
            )}

            {!isSearching && results.length === 0 && (
              <div className="card-placeholder">
                No results found
              </div>
            )}
          </section>
        )}

        {/* RELATED WORKS - Show when not searching and we have artwork data */}
        {!isSearchActive && artworkData && (
          <section className="search-results-section">
            <h3 className="section-title">
              {isLoadingRelated ? 'Loading...' : (
                gatewayInfo ? `${gatewayInfo.label} ${gatewayInfo.type === 'artist' ? 'works' : ''}` : `More by ${artworkData.artist?.name || 'this artist'}`
              )}
            </h3>

            {/* Loading overlay when loading related works and no results yet */}
            {isLoadingRelated && relatedWorks.length === 0 && (
              <div className="search-loading-overlay">
                <div className="search-loading-spinner" />
                <div className="search-loading-text">Finding related works...</div>
              </div>
            )}

            {relatedWorks.length > 0 && (
              <div className="search-results-grid">
                {relatedWorks.map((item, index) => (
                  <button
                    key={`${item.source}-${item.uri}-${index}`}
                    onClick={() => handleResultClick(item.uri, item.thumbnail)}
                    className="search-result-item"
                  >
                    <div className="result-thumbnail-wrapper">
                      <img
                        src={item.thumbnail}
                        alt={item.title}
                        className="result-thumbnail"
                        loading="lazy"
                      />
                      <span className={`source-badge source-${item.source}`}>
                        {item.source}
                      </span>
                    </div>
                    <div className="result-info">
                      <div className="result-title">{item.title}</div>
                      <div className="result-artist">{item.artist}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Infinite scroll sentinel for gateway results */}
            {gatewayHasMore && (
              <div ref={sentinelRef} className="scroll-sentinel">
                {isLoadingRelated && <span>Loading more...</span>}
              </div>
            )}

            {!isLoadingRelated && relatedWorks.length === 0 && (
              <div className="card-placeholder">
                No related works found
              </div>
            )}
          </section>
        )}

      </main>

      {/* Settings Panel */}
      {showSettings && (
        <div className="settings-panel" onClick={() => setShowSettings(false)}>
          <div className="settings-content" onClick={(e) => e.stopPropagation()}>
            <div className="settings-header">
              <h2 className="settings-title">Settings</h2>
              <button className="settings-close" onClick={() => setShowSettings(false)}>
                ✕
              </button>
            </div>

            <div className="settings-group">
              <label className="settings-label">
                Animation Duration: <span className="settings-value">
                  {settings.animationDuration >= 60
                    ? `${Math.floor(settings.animationDuration / 60)}m ${settings.animationDuration % 60}s`
                    : `${settings.animationDuration}s`}
                </span>
              </label>
              <input
                type="range"
                min="10"
                max="1800"
                step="10"
                value={settings.animationDuration}
                onChange={(e) => setSettings({ animationDuration: parseInt(e.target.value) })}
                className="settings-slider"
              />
              <div className="settings-hint">
                How long the Ken Burns pan effect takes (10s - 30 min)
              </div>
            </div>

            <div className="settings-divider" />

            <div className="settings-section-title">Slideshow Filters</div>

            <div className="settings-group">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <label className="settings-label">Filter by Movement</label>
                {settings.movementFilter && (
                  <button
                    className="filter-clear-x"
                    onClick={clearMovementFilter}
                    title="Clear movement filter"
                  >
                    ✕
                  </button>
                )}
              </div>
              <input
                type="text"
                value={draftFilters.movementFilter}
                onChange={(e) => setDraftFilters({ ...draftFilters, movementFilter: e.target.value })}
                placeholder="e.g., Impressionism, Cubism"
                className="settings-text-input"
              />
              <div className="settings-hint">
                {artworkData?.artist?.movements?.[0] && !draftFilters.movementFilter &&
                  `Current artwork: ${artworkData.artist.movements[0]}`}
                {!artworkData?.artist?.movements?.[0] && 'Leave empty to show all movements'}
              </div>
            </div>

            <div className="settings-group">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <label className="settings-label">Filter by Artist</label>
                {settings.artistFilter && (
                  <button
                    className="filter-clear-x"
                    onClick={clearArtistFilter}
                    title="Clear artist filter"
                  >
                    ✕
                  </button>
                )}
              </div>
              <input
                type="text"
                value={draftFilters.artistFilter}
                onChange={(e) => setDraftFilters({ ...draftFilters, artistFilter: e.target.value })}
                placeholder="e.g., Vincent van Gogh"
                className="settings-text-input"
              />
              <div className="settings-hint">
                {artworkData?.artist?.name && artworkData.artist.name !== 'Unknown' && !draftFilters.artistFilter &&
                  `Current artwork: ${artworkData.artist.name}`}
                {(!artworkData?.artist?.name || artworkData.artist.name === 'Unknown') && 'Leave empty to show all artists'}
              </div>
            </div>

            <div className="settings-group">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <label className="settings-label">Filter by Medium</label>
                {settings.mediumFilter && (
                  <button
                    className="filter-clear-x"
                    onClick={clearMediumFilter}
                    title="Clear medium filter"
                  >
                    ✕
                  </button>
                )}
              </div>
              <input
                type="text"
                value={draftFilters.mediumFilter}
                onChange={(e) => setDraftFilters({ ...draftFilters, mediumFilter: e.target.value })}
                placeholder="e.g., Oil on canvas, Watercolor"
                className="settings-text-input"
              />
              <div className="settings-hint">
                {artworkData?.details?.labelLine && !draftFilters.mediumFilter &&
                  `Current artwork: ${artworkData.details.labelLine}`}
                {!artworkData?.details?.labelLine && 'Leave empty to show all mediums'}
              </div>
            </div>

            <div className="settings-actions">
              <button
                className="settings-apply-btn"
                onClick={() => {
                  setSettings({
                    ...settings,
                    movementFilter: draftFilters.movementFilter,
                    artistFilter: draftFilters.artistFilter,
                    mediumFilter: draftFilters.mediumFilter
                  });
                  setShowSettings(false);
                }}
              >
                Apply Filters
              </button>
              {(draftFilters.movementFilter || draftFilters.artistFilter || draftFilters.mediumFilter) && (
                <button
                  className="settings-clear-filters"
                  onClick={() => setDraftFilters({
                    movementFilter: '',
                    artistFilter: '',
                    mediumFilter: ''
                  })}
                >
                  Clear All
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
